import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, mergeMap, withLatestFrom, filter } from 'rxjs/operators';
import { of } from 'rxjs';
import { CustomerAccountsService } from './customer-accounts.service';
import { AuthService } from '@ridango/core';
import { CustomerAccountTypeValue } from './customer-account.model';
import * as OrganizationsActions from '../organizations/organizations.actions';
import * as OrganizationPartActions from '../organization-parts/organization-part.actions';
import * as CustomerAccountsActions from './customer-accounts.actions';
import * as SELECTORS from './customer-accounts.selectors';

@Injectable()
export class CustomerAccountsEffects {
    loadCustomerAccounts$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CustomerAccountsActions.fetchCustomerAccounts),
            map(() => {
                const isAuthenticated = this.authService.getLoginStatus();
                return { isAuthenticated };
            }),
            filter(({ isAuthenticated }) => {
                return isAuthenticated;
            }),
            mergeMap(() => {
                return this.customerAccountsService.fetchCustomerAccounts().pipe(
                    map((accounts) => {
                        return CustomerAccountsActions.fetchCustomerAccountsSuccess({ accounts });
                    }),
                    catchError((error) => {
                        return of(CustomerAccountsActions.fetchCustomerAccountsFailed({ error }));
                    })
                );
            })
        );
    });

    setDefaultAccount$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CustomerAccountsActions.fetchCustomerAccountsSuccess),
            withLatestFrom(this.store.select(SELECTORS.getSelectedCustomerAccount)),
            // Only set default if no account is selected
            filter(([, selectedAccount]) => !selectedAccount),
            withLatestFrom(this.store.select(SELECTORS.getAllCustomerAccounts)),
            map(([, accounts]) => {
                if (accounts.length === 0) return null;
                // Try to find a personal account first, fallback to first account
                const personalAccount = accounts.find(
                    (account) => account.type.code === CustomerAccountTypeValue.PERSONAL
                );
                const defaultAccount = personalAccount || accounts[0];
                return CustomerAccountsActions.selectCustomerAccount({ accountId: defaultAccount.id.toString() });
            }),
            filter((action) => action !== null)
        );
    });

    fetchOrganizationPartsForAccounts$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CustomerAccountsActions.fetchCustomerAccountsSuccess),
            map(({ accounts }) => {
                const organizationPartIds = accounts
                    .filter(
                        (account): account is typeof account & { organizationPartId: number } =>
                            account.organizationPartId !== null
                    )
                    .map((account) => account.organizationPartId)
                    .filter((id, index, arr) => arr.indexOf(id) === index);

                if (organizationPartIds.length > 0) {
                    return OrganizationPartActions.fetchOrganizationPartsByIds({ organizationPartIds });
                }
                return null;
            }),
            filter((action) => action !== null)
        );
    });

    // Effect to fetch parent organizations when organization parts are loaded
    fetchParentOrganizations$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OrganizationPartActions.fetchOrganizationPartsByIdsSuccess),
            map(({ organizationParts }) => {
                const parentOrganizationIds = organizationParts
                    .map((part) => part.parentOrganizationId)
                    .filter((id, index, arr) => arr.indexOf(id) === index); // Remove duplicates

                if (parentOrganizationIds.length > 0) {
                    return OrganizationsActions.fetchOrganizationsByIds({ organizationIds: parentOrganizationIds });
                }
                return null;
            }),
            filter((action) => action !== null)
        );
    });

    constructor(
        private actions$: Actions,
        private store: Store,
        private customerAccountsService: CustomerAccountsService,
        private authService: AuthService
    ) {}
}
